import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import DeleteIcon from 'assets/settings/components/DeleteIcon';
import EditIcon from 'assets/settings/components/EditIcon';
import Select from 'common-ui/select/Select';
import Pagination from 'common-ui/table/Pagination';
import {
  getAdministrativeRegions,
  getCities,
  getStreets,
} from 'features/newsales/newsalesActions';
import {
  selectStreets,
  selectStreetsCurrentPage,
  selectStreetsDeleteSuccess,
  selectStreetsLoading,
  selectStreetsPagesCount,
  selectStreetsSearchData,
  selectStreetsSelectedCityId,
  selectStreetsSelectedCountryId,
  selectStreetsSelectedDistrictId,
  selectStreetsSuccess,
  selectStreetsUpdateSuccess,
  setStreetsCreateSuccess,
  setStreetsCurrentPage,
  setStreetsDeleteSuccess,
  setStreetsSearchData,
  setStreetsSelectedCity,
  setStreetsSelectedCountry,
  setStreetsSelectedDistrict,
  setStreetsUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import {
  ActionsCell,
  EmptyState,
  FilterFlex,
  FilterSection,
  IconButton,
  LoadingSpinner,
  PaginationWrapper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderActions,
  TableRow,
} from '../Addresses.styles';

const StreetPage = ({ onEdit, onDelete, allCountries }) => {
  const dispatch = useDispatch();
  const [regionsForStreets, setRegionsForStreets] = useState([]);
  const [citiesForStreets, setCitiesForStreets] = useState([]);

  const streets = useSelector(selectStreets);
  const streetsLoading = useSelector(selectStreetsLoading);
  const streetsCurrentPage = useSelector(selectStreetsCurrentPage);
  const streetsPagesCount = useSelector(selectStreetsPagesCount);
  const streetsSearchData = useSelector(selectStreetsSearchData);
  const streetsCreateSuccess = useSelector(selectStreetsSuccess);
  const streetsUpdateSuccess = useSelector(selectStreetsUpdateSuccess);
  const streetsDeleteSuccess = useSelector(selectStreetsDeleteSuccess);
  const streetSelectedCountryId = useSelector(selectStreetsSelectedCountryId);
  const streetSelectedDistrictId = useSelector(selectStreetsSelectedDistrictId);
  const streetSelectedCityId = useSelector(selectStreetsSelectedCityId);

  useEffect(() => {
    if (streetSelectedCountryId) {
      dispatch(
        getAdministrativeRegions({
          countryId: streetSelectedCountryId,
          limit: 1000,
        })
      ).then((result) => {
        if (result.payload && result.payload.data) {
          setRegionsForStreets(result.payload.data);
        }
      });
    }
  }, [dispatch, streetSelectedCountryId]);

  useEffect(() => {
    if (streetSelectedCountryId && streetSelectedDistrictId) {
      dispatch(
        getCities({
          countryId: streetSelectedCountryId,
          districtId: streetSelectedDistrictId,
          limit: 1000,
        })
      ).then((result) => {
        if (result.payload && result.payload.data) {
          setCitiesForStreets(result.payload.data);
        }
      });
    }
  }, [dispatch, streetSelectedCountryId, streetSelectedDistrictId]);

  useEffect(() => {
    if (streetSelectedCountryId && streetSelectedDistrictId && streetSelectedCityId) {
      dispatch(
        getStreets({
          countryId: streetSelectedCountryId,
          districtId: streetSelectedDistrictId,
          cityId: streetSelectedCityId,
          page: streetsCurrentPage,
          limit: streetsSearchData.limit,
          search: streetsSearchData.search,
        })
      );
    }
  }, [
    dispatch,
    streetSelectedCountryId,
    streetSelectedDistrictId,
    streetSelectedCityId,
    streetsCurrentPage,
    streetsSearchData.limit,
    streetsSearchData.search,
  ]);

  useEffect(() => {
    if (streetsCreateSuccess) {
      dispatch(setStreetsCreateSuccess(false));
      if (streetSelectedCountryId && streetSelectedDistrictId && streetSelectedCityId) {
        dispatch(
          getStreets({
            countryId: streetSelectedCountryId,
            districtId: streetSelectedDistrictId,
            cityId: streetSelectedCityId,
            page: streetsCurrentPage,
            limit: streetsSearchData.limit,
            search: streetsSearchData.search,
          })
        );
      }
    }
  }, [
    streetsCreateSuccess,
    dispatch,
    streetSelectedCountryId,
    streetSelectedDistrictId,
    streetSelectedCityId,
    streetsCurrentPage,
    streetsSearchData.limit,
    streetsSearchData.search,
  ]);

  useEffect(() => {
    if (streetsUpdateSuccess) {
      dispatch(setStreetsUpdateSuccess(false));
    }
  }, [streetsUpdateSuccess, dispatch]);

  useEffect(() => {
    if (streetsDeleteSuccess) {
      dispatch(setStreetsDeleteSuccess(false));
      if (streets?.length === 1 && streetsCurrentPage > 1) {
        dispatch(setStreetsCurrentPage(streetsCurrentPage - 1));
      } else {
        // Refetch current page data after deletion
        if (streetSelectedCountryId && streetSelectedDistrictId && streetSelectedCityId) {
          dispatch(
            getStreets({
              countryId: streetSelectedCountryId,
              districtId: streetSelectedDistrictId,
              cityId: streetSelectedCityId,
              page: streetsCurrentPage,
              limit: streetsSearchData.limit,
              search: streetsSearchData.search,
            })
          );
        }
      }
    }
  }, [
    streetsDeleteSuccess,
    dispatch,
    streets,
    streetsCurrentPage,
    streetSelectedCountryId,
    streetSelectedDistrictId,
    streetSelectedCityId,
    streetsSearchData.limit,
    streetsSearchData.search,
  ]);

  const handleStreetCountrySelect = (selectedOption) => {
    const countryId = selectedOption ? selectedOption.value : null;
    dispatch(setStreetsSelectedCountry(countryId));
  };

  const handleStreetDistrictSelect = (selectedOption) => {
    const districtId = selectedOption ? selectedOption.value : null;
    dispatch(setStreetsSelectedDistrict(districtId));
  };

  const handleStreetCitySelect = (selectedOption) => {
    const cityId = selectedOption ? selectedOption.value : null;
    dispatch(setStreetsSelectedCity(cityId));
  };

  const handlePageChange = (page) => {
    dispatch(setStreetsCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setStreetsSearchData({ limit: count, offset: 0 }));
    dispatch(setStreetsCurrentPage(1));
  };

  const streetCountryOptions = allCountries.map((country) => ({
    value: country.id,
    label: country.name,
  }));

  const streetRegionOptions = regionsForStreets.map((region) => ({
    value: region.id,
    label: region.name,
  }));

  const streetCityOptions = citiesForStreets.map((city) => ({
    value: city.id,
    label: city.name,
  }));

  const streetSelectedCountryOption = streetSelectedCountryId
    ? streetCountryOptions.find((option) => option.value === streetSelectedCountryId)
    : null;

  const streetSelectedDistrictOption = streetSelectedDistrictId
    ? streetRegionOptions.find((option) => option.value === streetSelectedDistrictId)
    : null;

  const streetSelectedCityOption = streetSelectedCityId
    ? streetCityOptions.find((option) => option.value === streetSelectedCityId)
    : null;

  return (
    <>
      <FilterFlex>
        <FilterSection $noBorder>
          <Select
            label="Select Country"
            value={streetSelectedCountryOption}
            onChange={handleStreetCountrySelect}
            options={streetCountryOptions}
            placeholder="Select a country"
            isClearable
            isDisabled={allCountries.length === 0}
          />
        </FilterSection>
        <FilterSection $noBorder>
          <Select
            label="Select Region"
            value={streetSelectedDistrictOption}
            onChange={handleStreetDistrictSelect}
            options={streetRegionOptions}
            placeholder="Select a region"
            isClearable
            isDisabled={!streetSelectedCountryId || regionsForStreets.length === 0}
          />
        </FilterSection>
        <FilterSection>
          <Select
            label="Select City/Village"
            value={streetSelectedCityOption}
            onChange={handleStreetCitySelect}
            options={streetCityOptions}
            placeholder="Select a city"
            isClearable
            isDisabled={!streetSelectedDistrictId || citiesForStreets.length === 0}
          />
        </FilterSection>
      </FilterFlex>

      {streetsLoading ? (
        <LoadingSpinner>Loading...</LoadingSpinner>
      ) : !streetSelectedCountryId ? (
        <EmptyState>Please select a country to view streets.</EmptyState>
      ) : !streetSelectedDistrictId ? (
        <EmptyState>Please select a region to view streets.</EmptyState>
      ) : !streetSelectedCityId ? (
        <EmptyState>Please select a city to view streets.</EmptyState>
      ) : !streets || streets.length === 0 ? (
        <EmptyState>No streets found.</EmptyState>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeaderActions>Actions</TableHeaderActions>
              </TableRow>
            </TableHead>
            <TableBody>
              {streets.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <ActionsCell>
                    <IconButton onClick={() => onEdit(item)}>
                      <EditIcon size={18} color="#2D6CDF" />
                    </IconButton>
                    <IconButton onClick={() => onDelete(item)}>
                      <DeleteIcon size={18} color="#FB2C36" />
                    </IconButton>
                  </ActionsCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {streetsPagesCount > 1 && (
            <PaginationWrapper>
              <Pagination
                currentPage={streetsCurrentPage}
                totalPages={streetsPagesCount}
                onPageChange={handlePageChange}
                handleRowCountChange={handleRowCountChange}
                dataCount={streets.length}
                count={streetsSearchData.limit}
                editableRowCount
              />
            </PaginationWrapper>
          )}
        </>
      )}
    </>
  );
};

export default StreetPage;
